import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import MeaLink from "../../../../components/MeaLink";
import paymentsAPI from "../../../../services/payments";
import uiDataAPI from "../../../../services/ui_data";
import { PERMISSION_TRAINING_CONTRACTS } from "../../../../permissions";
import { useAuthContext } from "../../../../hooks/useAuthContext";

const LatestPaymentsModal = forwardRef((props, ref) => {
    const { schoolId, trainingId } = props;
    const { auth } = useAuthContext();
    const accessToken = auth?.access_token || "";

    const [visible, setVisible] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedMethod, setSelectedMethod] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");

    // Fetch payments list with React Query
    const paymentsQuery = paymentsAPI.useGetList(
        schoolId,
        trainingId,
        accessToken,
        page,
        pageSize,
        {
            method: selectedMethod,
            status: selectedStatus,
            sort: "updated_at desc",
        }
    );

    // Fetch metadata for dropdowns
    const paymentMethodsQuery = uiDataAPI.UiDataPaymentMethodTypesList(accessToken);
    const paymentStatusQuery = uiDataAPI.UiDataPaymentStatusTypesList(accessToken);

    useImperativeHandle(ref, () => ({
        show: () => {
            setVisible(true);
            paymentsQuery.refetch();
        },
        hide: () => setVisible(false),
    }));

    const onPage = (event) => {
        setPage(event.page + 1);
        setPageSize(event.rows);
    };

    const handleClearFilters = () => {
        setSelectedMethod("");
        setSelectedStatus("");
        setPage(1);
    };

    const getMethodIcon = (methodId) => {
        if (!methodId) return "pi pi-wallet";
        const mapping = {
            CASH: "pi pi-euro",
            CB: "pi pi-credit-card",
            CHEQUE: "pi pi-wallet",
            HELLOASSO: "pi pi-link",
            OTHER: "pi pi-align-justify",
        };
        return mapping[methodId] || "pi pi-wallet";
    };

    const getStatusMeta = (statusId) => {
        const mapping = {
            PAID: {
                severity: "success",
                label: "Encaissé",
                className: "bg-green-50 text-green-700 border-green-100",
            },
            PENDING: {
                severity: "warning",
                label: "En attente d'encaissement",
                className: "bg-orange-50 text-orange-700 border-orange-100",
            },
            REFUNDED: {
                severity: "info",
                label: "Remboursé",
                className: "bg-blue-50 text-blue-700 border-blue-100",
            },
            CONTESTED: {
                severity: "danger",
                label: "Contesté",
                className: "bg-red-50 text-red-700 border-red-100",
            },
            REFUSED: {
                severity: "danger",
                label: "Refusé",
                className: "bg-red-50 text-red-700 border-red-100",
            },
            UNKNOWN: {
                severity: "secondary",
                label: "Inconnu",
                className: "bg-gray-50 text-gray-700 border-gray-100",
            }
        };
        return mapping[statusId] || { severity: "info", label: statusId, className: "" };
    };

    // Columns templates
    const contractTemplate = (rowData) => {
        const contract = rowData?.contract;
        if (!contract) return <span>-</span>;
        return (
            <MeaLink
                to={`/school/${schoolId}/admin/registrations/${trainingId}/contracts/${contract.id}`}
                permission={PERMISSION_TRAINING_CONTRACTS.GET}
            >
                <span className="font-semibold text-primary hover:underline">{contract.name}</span>
                <span className="text-500 text-xs ml-1 font-mono">({contract.id})</span>
            </MeaLink>
        );
    };

    const methodTemplate = (rowData) => {
        const methodId = rowData?.method;
        const methodObj = paymentMethodsQuery.data?.data?.find((m) => m.id === methodId);
        const methodName = methodObj?.name || methodId || "Autre";
        const iconClass = getMethodIcon(methodId);
        return (
            <div className="flex align-items-center gap-2">
                <i className={`${iconClass} text-primary-500 text-base`} />
                <span className="text-800 text-sm font-medium">{methodName}</span>
            </div>
        );
    };

    const amountTemplate = (rowData) => {
        const amountFormatter = new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
        });
        return (
            <span className="font-bold text-primary-600 text-sm">
                {amountFormatter.format(rowData.amount)}
            </span>
        );
    };

    const statusTemplate = (rowData) => {
        const meta = getStatusMeta(rowData.status);
        return (
            <Tag
                severity={meta.severity}
                value={meta.label}
                className={`${meta.className} border-round-md border-1 font-semibold px-2.5 py-1 text-xs`}
            />
        );
    };

    const updatedDateTemplate = (rowData) => {
        if (!rowData?.updated_at) return <span>-</span>;
        const date = new Date(rowData.updated_at);
        return (
            <span className="text-sm text-800">
                {date.toLocaleDateString("fr-FR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                })}
            </span>
        );
    };

    const methodOptions = [
        { name: "Tous les types", id: "" },
        ...(paymentMethodsQuery.data?.data || []),
    ];

    const statusOptions = [
        { name: "Tous les statuts", id: "" },
        ...(paymentStatusQuery.data?.data || []),
    ];

    const hasActiveFilters = selectedMethod !== "" || selectedStatus !== "";

    return (
        <Dialog
            header="Derniers paiements effectués"
            visible={visible}
            onHide={() => setVisible(false)}
            style={{ width: "65vw", maxWidth: "1000px" }}
            breakpoints={{ "960px": "85vw", "641px": "95vw" }}
            footer={
                <Button
                    label="Fermer"
                    icon="pi pi-times"
                    onClick={() => setVisible(false)}
                    className="p-button-outlined"
                />
            }
        >
            <div className="flex flex-column gap-3">
                {/* Filters Panel */}
                <div className="flex flex-wrap align-items-end justify-content-between gap-3 p-3 bg-gray-50 border-round border-1 border-gray-100">
                    <div className="flex flex-wrap gap-3">
                        <div className="flex flex-column gap-1">
                            <span className="text-xs font-semibold text-600">Type de paiement</span>
                            <Dropdown
                                value={selectedMethod}
                                options={methodOptions}
                                onChange={(e) => {
                                    setSelectedMethod(e.value);
                                    setPage(1);
                                }}
                                optionLabel="name"
                                optionValue="id"
                                placeholder="Tous les types"
                                className="w-13rem"
                            />
                        </div>
                        <div className="flex flex-column gap-1">
                            <span className="text-xs font-semibold text-600">Statut du paiement</span>
                            <Dropdown
                                value={selectedStatus}
                                options={statusOptions}
                                onChange={(e) => {
                                    setSelectedStatus(e.value);
                                    setPage(1);
                                }}
                                optionLabel="name"
                                optionValue="id"
                                placeholder="Tous les statuts"
                                className="w-13rem"
                            />
                        </div>
                    </div>
                    {hasActiveFilters && (
                        <Button
                            icon="pi pi-filter-slash"
                            label="Réinitialiser"
                            onClick={handleClearFilters}
                            className="p-button-text p-button-sm"
                        />
                    )}
                </div>

                {/* Table */}
                <DataTable
                    value={paymentsQuery.data?.data || []}
                    lazy
                    paginator
                    first={(page - 1) * pageSize}
                    rows={pageSize}
                    totalRecords={paymentsQuery.data?.total || 0}
                    onPage={onPage}
                    loading={paymentsQuery.isLoading}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    className="p-datatable-sm"
                    emptyMessage="Aucun paiement enregistré pour cette formation."
                >
                    <Column header="Famille (N° Contrat)" body={contractTemplate} style={{ width: "25%" }} />
                    <Column header="Type de paiement" body={methodTemplate} style={{ width: "20%" }} />
                    <Column header="Montant" body={amountTemplate} style={{ width: "15%", textAlign: "right" }} />
                    <Column header="Statut du paiement" body={statusTemplate} style={{ width: "20%", textAlign: "center" }} />
                    <Column header="Date de modification" body={updatedDateTemplate} style={{ width: "20%" }} />
                </DataTable>
            </div>
        </Dialog>
    );
});

LatestPaymentsModal.displayName = "LatestPaymentsModal";

export default LatestPaymentsModal;
